import { withBase } from './brand';
import imgixLoader from '../utils/imageLoader';

export const REGION_IMAGE_FALLBACK = '/images/default-campsite-hero.jpg';

/**
 * Resolve a Region's `image_src` to a URL this app can actually render.
 *
 * `image_src` arrives from Neo4j in one of three shapes:
 *   - an absolute URL   ("https://app.trip-planner.com.au/media/commons-images/...",
 *                        or a legacy third-party URL)          → use as-is
 *   - an app-relative path ("/images/eyrepeninsula.jpg")      → needs withBase
 *   - null / empty                                            → fallback hero
 *
 * The relative form is what we use for regions whose upstream photo has rotted;
 * the file ships in `public/images/`. It has to go through withBase() because
 * camping-australia serves the app under /apps/campsite-explorer — a bare
 * "/images/x.jpg" 404s there. withBase() exempts asset paths from EscapeTo's
 * country prefix, so the same stored value resolves correctly on both brands.
 */
export function resolveRegionImage(imageSrc?: string | null): string {
  const src = (imageSrc || '').trim();
  if (!src) return withBase(REGION_IMAGE_FALLBACK);
  if (/^https?:\/\//i.test(src)) return src;
  return withBase(src.startsWith('/') ? src : `/${src}`);
}

/** Our own media host (Caddy file_server); jola-media imgix resizes it. */
const MEDIA_ORIGIN = 'https://app.trip-planner.com.au/media/';

export function isOwnMedia(src?: string | null): boolean {
  return !!src && src.startsWith(MEDIA_ORIGIN);
}

/**
 * True for third-party URLs, which Next's loader cannot resize. Our own media
 * (the mirrored Wikimedia copies included) is not "remote": the custom loader
 * sends it through imgix at the requested width.
 */
export function isRemoteImage(src: string): boolean {
  return /^https?:\/\//i.test(src) && !isOwnMedia(src);
}

/** A width-capped rendition for plain <img> tags; non-media URLs unchanged. */
export function sizedMedia(src: string, width: number): string {
  return isOwnMedia(src) ? imgixLoader({ src, width }) : src;
}

/**
 * Credit line for a region photo. The attribution field was introduced for
 * Unsplash (US regions), but CA/NZ/AR/CL and mirrored AU photos come from
 * Wikimedia Commons under CC licences, and were being labelled "on Unsplash".
 */
export function regionPhotoCredit(region: {
  image_attribution?: string | null;
  image_attribution_url?: string | null;
}): { text: string; href?: string; source: 'Unsplash' | 'Wikimedia Commons' | null } | null {
  const text = (region.image_attribution || '').trim();
  if (!text) return null;
  const href = region.image_attribution_url || undefined;
  if (href && /unsplash\.com/i.test(href)) return { text, href, source: 'Unsplash' };
  if (href && /(wikimedia|wikipedia|creativecommons)\.org/i.test(href)) return { text, href, source: 'Wikimedia Commons' };
  return { text, href, source: null };
}

/**
 * Compact credit for a region photo in a list of several ("Where to next"):
 * "JJ Harrison, CC BY-SA 3.0, via Wikimedia Commons". Commons attributions
 * arrive as "Author (homepage) / Licence"; the homepage is dropped here (the
 * link goes to the file's Commons page, which carries it). Null without one.
 */
export function regionCreditLabel(region: {
  image_attribution?: string | null;
  image_attribution_url?: string | null;
}): { label: string; href?: string } | null {
  const c = regionPhotoCredit(region);
  if (!c) return null;
  const text = c.text.replace(/\s*\(\s*https?:\/\/[^)]*\)/gi, '').replace(/\s+/g, ' ').trim();
  if (c.source === 'Unsplash') return { label: `${text} on Unsplash`, href: c.href };
  if (c.source === 'Wikimedia Commons') {
    const i = text.lastIndexOf(' / ');
    const body = i > 0 ? `${text.slice(0, i).trim()}, ${text.slice(i + 3).trim()}` : text;
    return { label: /wikimedia commons$/i.test(body) ? body : `${body}, via Wikimedia Commons`, href: c.href };
  }
  return { label: text, href: c.href };
}
